/**
 * 이용 내역·권한·지원 접속·검사 모드 계약 API 를 부르는 곳(W6 3장 — 앱마다 한 파일).
 * 경로·모양은 inapi 계약 커밋 a6e5c7ab9 의 docs/standards/(SUPPORT 9 · AUDIT 6-2·6-3 · PERMISSIONS 7 · README 「모드 스위치」).
 * 서버(W1·W2)가 늦거나 모양이 다르면 이 파일만 고친다.
 */
import apiInstance from "@/axios";
import store from "@/store";
import { createOnceWithin } from "@/utils/standardErrors";
import { PERM_APPS, mergePermLabels } from "@/utils/perm";

// 화면이 스스로 부르는 조회라 전역 로딩·알림·step-up 창을 띄우지 않는다.
// skipPermReload: 이 요청들이 403 PERMISSION_DENIED 를 받아도 권한 다시 읽기를 또 부르지 않음(자기 자신을 기다려 멈춤)
const quiet = { showGlobalLoading: false, showErrorNotice: false, skipStepUp: true, skipPermReload: true };

/** 권한·모드 값(profile.perms·modes). 실패하면 빈 값({} — 다시 부르지 않음)으로 두고 화면은 숨기지 않는다 */
export async function loadIntrospect() {
  try {
    const { data } = await apiInstance.get("/auth/introspect", quiet);
    store.commit("setIntrospect", data || {});
    return data;
  } catch (e) {
    store.commit("setIntrospect", {});
    return null;
  }
}

/** 한 앱의 권한 이름표 원본(GET /perm-catalog?app=). 못 받으면 null — 이름표는 문구용이라 없어도 된다 */
const permCatalog = (app) =>
  apiInstance.get("/perm-catalog", { ...quiet, params: { app } }).then(
    ({ data }) => data,
    () => null
  );

/** 이 앱 권한 이름표 {code: ko}. 못 받으면 그대로 둠(문구에 코드가 그대로 나감) */
export async function loadPermLabels() {
  const data = await permCatalog("admin-certilife");
  if (data) store.commit("setPermLabels", mergePermLabels([data]));
}

/**
 * 모든 앱 권한 이름표 {code: ko}(「막혔을 요청」 — 고객사 앱 코드 settings.channel.view 등도 나옴, 10-31 intest).
 * 카탈로그가 앱 하나씩만 내려줘서 앱마다 한 번씩 부르고 합친다. 못 받은 앱은 빼고 합침
 */
export const loadAllPermLabels = () => Promise.all(PERM_APPS.map(permCatalog)).then(mergePermLabels);

/** 403 PERMISSION_DENIED 뒤 한 번만(30초 안 반복 금지) 권한을 다시 읽는다 */
export const reloadPerms = createOnceWithin(() => Promise.all([loadIntrospect(), loadPermLabels()]), 30000);

const listOf = (data) => (Array.isArray(data) ? data : (data && (data.items || data.rows)) || []);

/** 검사 모드 스위치(README 「모드 스위치」, W1). 바꾸기는 M! — 증표가 없으면 axios 인터셉터가 본인 확인 창을 띄우고 다시 보냄 */
const SETTINGS = "/admin/security/settings";
export const SecuritySettingsApi = {
  list: () => apiInstance.get(SETTINGS, { showGlobalLoading: false }).then(({ data }) => listOf(data)),
  put: (key, body) => apiInstance.put(`${SETTINGS}/${encodeURIComponent(key)}`, body).then(({ data }) => data),
  history: (key) =>
    apiInstance
      .get(`${SETTINGS}/${encodeURIComponent(key)}/history`, { showGlobalLoading: false })
      .then(({ data }) => listOf(data)),
};

/**
 * 로그인 뒤 권한 재검토(T-CONTRACT-1031 §2). 실패는 조용히(알림·전역 로딩 없음) — 부르는 쪽이 넘어간다.
 * 기록(POST)은 서버가 본인 확인을 요구하면 step-up 창을 띄운다(사람이 누른 동작)
 */
export const PermReviewApi = {
  status: () => apiInstance.get("/perm-reviews/status", quiet).then(({ data }) => data || {}),
  record: (body) =>
    apiInstance
      .post("/perm-reviews", body, { showGlobalLoading: false, showErrorNotice: false, skipPermReload: true })
      .then(({ data }) => data),
};

/** 「막혔을 요청」(T-CONTRACT-1031 §3) {kind, q, cursor, limit} → {items, next}. 개인정보 없음 */
export const PermObservationApi = {
  list: (params) =>
    apiInstance.get("/admin/perm/observations", { showGlobalLoading: false, params }).then(({ data }) => ({
      items: listOf(data),
      next: (data && data.next) || null,
    })),
};

/** 「고객 지원을 위한 접근 허용」 기존 회사 일괄 켜기(사용자 결정 10-29). 실행은 M! — 인터셉터가 본인 확인 창 */
export const ConsentBulkApi = {
  preview: () => apiInstance.get("/admin/support/consent-bulk", { showGlobalLoading: false }).then(({ data }) => data || null),
  run: (body) => apiInstance.post("/admin/support/consent-bulk", body).then(({ data }) => data || {}),
};

/** 이용 내역 조회·내려받기(AUDIT 6-2, W1) */
export const AuditApi = {
  actions: () => apiInstance.get("/audit/actions", quiet).then(({ data }) => data || {}),
  search: (body) => apiInstance.post("/audit/events/search", body, { showGlobalLoading: false }).then(({ data }) => data || {}),
  // 운영자는 회사를 같이 주면 그 회사 인덱스로 찾는다(W1 GET /audit/events/:id?companyId=)
  get: (id, companyId) =>
    apiInstance
      .get(`/audit/events/${encodeURIComponent(id)}`, { showGlobalLoading: false, params: companyId ? { companyId } : undefined })
      .then(({ data }) => data),
  /** 서버가 만든 CSV(브라우저에서 만들지 않음). 응답 그대로(헤더의 파일 이름을 읽으려고) */
  export: (body) => apiInstance.post("/audit/events/export", body, { responseType: "blob" }),
  /**
   * 삭제(계약 AUDIT 「이용 내역 삭제」) → {deleted, protected, notFound}. 증표는 본문 mfaToken 이라 step-up 창으로 다시 보내지 않고,
   * 실패 문구는 삭제 창 안에서 보인다
   */
  remove: (body) =>
    apiInstance
      .post("/admin/audit/events/delete", body, { showGlobalLoading: false, showErrorNotice: false, skipStepUp: true })
      .then(({ data }) => data),
};

/** 응답 blob 을 파일로 저장. 이름은 Content-Disposition 이 있으면 그것 */
export function saveBlobResponse(res, fallbackName) {
  const cd = (res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"])) || "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const url = URL.createObjectURL(res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const formData = (fields) => {
  const fd = new FormData();
  fields.forEach(([k, v]) => fd.append(k, v));
  return fd;
};

/**
 * 회사 상세 「고객 지원을 위한 접근」(SUPPORT 9, W1). 회사 상세는 부트스트랩 창이라 전역 알림이 뒤에 가리므로
 * 실패는 카드 안 줄에만 보인다(showErrorNotice:false). M! 경로는 axios 인터셉터가 본인 확인 창을 띄우고 다시 보냄.
 */
const supportBase = (companyId) => `/admin/companies/${encodeURIComponent(companyId)}/support`;
const inCard = { showGlobalLoading: false, showErrorNotice: false };
export const SupportAdminApi = {
  /** 위쪽 「지원 접속」 검색(10-29) {q} → [{_id, name, type, adminUsername}] — 검색어는 본문으로 */
  findTargets: (q) =>
    apiInstance
      .post("/admin/support/companies/search", { q }, { showGlobalLoading: false, showErrorNotice: false })
      .then(({ data }) => (Array.isArray(data) ? data : [])),
  status: (companyId) => apiInstance.get(supportBase(companyId), inCard).then(({ data }) => data || {}),
  offlineConsent: (companyId, fields) =>
    apiInstance.post(`${supportBase(companyId)}/consent/offline`, formData(fields), inCard).then(({ data }) => data),
  revoke: (companyId, req) =>
    apiInstance
      .post(`${supportBase(companyId)}/consent/revoke`, req.fields ? formData(req.fields) : req.json, inCard)
      .then(({ data }) => data),
  evidence: (companyId) => apiInstance.get(`${supportBase(companyId)}/consent/evidence`, { ...inCard, responseType: "blob" }),
  lock: (companyId, body) => apiInstance.post(`${supportBase(companyId)}/sub-account/lock`, body, inCard).then(({ data }) => data),
  unlock: (companyId, body) => apiInstance.post(`${supportBase(companyId)}/sub-account/unlock`, body, inCard).then(({ data }) => data),
  /** 동의 스위치(CONSENT-1028) {status, reason} → Consent. 본인 확인 기록이 없으면 403 SUPPORT_PROOF_REQUIRED */
  setConsent: (companyId, body) =>
    apiInstance.put(`/support/companies/${encodeURIComponent(companyId)}/consent`, body, inCard).then(({ data }) => data),
  /** 201 {code, app, expiresInSec:60} */
  startSession: (body) => apiInstance.post("/support/sessions", body, inCard).then(({ data }) => data),
};

/** 지원 진입 본인 확인(계약 20de33c3d): 기존 step-up(LOGIN) 으로 증표 → POST /support/proof {method:'mfa', mfaToken} */
export const SupportProofApi = {
  factors: () => apiInstance.get("/auth/profile", { showGlobalLoading: false }).then(({ data }) => (data && data.mfa) || {}),
  challenge: (purpose, method) =>
    apiInstance.post("/auth/2fa/step-up/challenge", { purpose, method }, { showGlobalLoading: false }).then(({ data }) => data || {}),
  verify: (purpose, method, code) =>
    apiInstance.post("/auth/2fa/step-up/verify", { purpose, method, code }, { showGlobalLoading: false }).then(({ data }) => data || {}),
  proof: (body) => apiInstance.post("/support/proof", body, inCard).then(({ data }) => data),
};

/** 월 점검(AUDIT 6-3, W1). 저장은 필수 이벤트 audit.check.record — 기록 실패면 503 AUDIT_UNAVAILABLE */
export const AuditCheckApi = {
  summary: (month) => apiInstance.get("/admin/audit/checks", { showGlobalLoading: false, params: { month } }).then(({ data }) => data || {}),
  save: (body) => apiInstance.post("/admin/audit/checks", body).then(({ data }) => data),
  history: () => apiInstance.get("/admin/audit/checks/history", { showGlobalLoading: false }).then(({ data }) => listOf(data)),
};
